import {
    Box,
    CircularProgress,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip as MuiTooltip,
    Typography
} from "@material-ui/core";
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { useEffect, useMemo, useState } from "react";
import { useAppState } from "../context/zustand";
import { useEpisodeSelectionConsumer } from "../hooks/useEpisodeSelectionConsumer";
import { compareEpisodeNumbersDescending, getEpisodeNumber } from "../utils/episodeNumber";
import { EPISODE_MODE_NONE, getEpisodeSelectionRequest } from "../utils/episodeSelection";
import { buildGraphCsvFilename, downloadGraphCsv, getCsvDateRange, getEpisodeMetadataMap } from "../utils/graphCsv";
import { useAxios } from "../utils/request";
import { getPlatformDisplayName } from "../utils/utils";

const TABLE_PLATFORM_KEYS = ['blubrry', 'spotify', 'apple'];
const COMPACT_COLUMN_KEYS = ['episodeNumber', 'seasonNumber'];

const TABLE_COLUMNS = [
    { key: 'episodeNumber', header: 'Episode number', sortType: 'episodeNumber' },
    { key: 'seasonNumber', header: 'Season number', sortType: 'number' },
    { key: 'episodeTitle', header: 'Episode title', sortType: 'text' },
    { key: 'duration', header: 'Duration', sortType: 'duration' },
    { key: 'release_date', header: 'Release date', sortType: 'date' },
    ...TABLE_PLATFORM_KEYS.map((key) => ({
        key,
        header: getPlatformDisplayName(key),
        numeric: true,
        sortType: 'number',
    })),
    { key: 'total', header: 'Total', numeric: true, sortType: 'number' },
];

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        minHeight: 520,
        padding: theme.spacing(4),
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
    },
    tableContainer: {
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    tableHeadCell: {
        cursor: 'pointer',
        fontWeight: 600,
        lineHeight: 1.15,
        overflow: 'hidden',
        whiteSpace: 'normal',
        '&:hover': {
            backgroundColor: '#f8fafc',
        },
    },
    activeTableHeadCell: {
        color: '#101828',
        fontWeight: 800,
    },
    tableHeadContent: {
        alignItems: 'center',
        display: 'inline-flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        justifyContent: 'center',
        lineHeight: 1.15,
        minHeight: 58,
        textAlign: 'center',
        width: '100%',
    },
    tableHeadLabel: {
        display: 'inline-block',
    },
    sortIndicator: {
        alignItems: 'center',
        color: '#475467',
        display: 'inline-flex',
        flexDirection: 'column',
        lineHeight: 0.7,
        minWidth: 24,
    },
    activeSortIndicator: {
        color: '#d92d20',
    },
    sortIcon: {
        fontSize: 34,
        height: 16,
        width: 28,
    },
    inactiveSortIcon: {
        color: '#475467',
        opacity: 1,
    },
    mutedSortIcon: {
        color: '#98a2b3',
    },
    activeSortIcon: {
        color: '#d92d20',
        fontSize: 38,
    },
    titleCell: {
        minWidth: 240,
    },
    compactCell: {
        maxWidth: 74,
        minWidth: 64,
        paddingLeft: theme.spacing(0.75),
        paddingRight: theme.spacing(0.75),
        width: 70,
    },
    platformCell: {
        maxWidth: 82,
        minWidth: 72,
        paddingLeft: theme.spacing(0.75),
        paddingRight: theme.spacing(0.75),
        width: 78,
    },
    numericCell: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
    },
    emptyState: {
        minHeight: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.text.secondary,
    },
    loadingState: {
        minHeight: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    downloadButton: {
        padding: theme.spacing(1),
    },
}));

function getColumnClasses(column, classes) {
    return [
        column.key === 'episodeTitle' ? classes.titleCell : '',
        COMPACT_COLUMN_KEYS.includes(column.key) ? classes.compactCell : '',
        TABLE_PLATFORM_KEYS.includes(column.key) ? classes.platformCell : '',
        column.numeric ? classes.numericCell : '',
    ].filter(Boolean).join(' ');
}

function renderHeader(header) {
    return String(header).split(' ').map((part, index) => (
        <React.Fragment key={`${header}-${part}-${index}`}>
            {index > 0 && <br />}
            {part}
        </React.Fragment>
    ));
}

function SortIndicator({ active, order, classes }) {
    return (
        <span className={`${classes.sortIndicator} ${active ? classes.activeSortIndicator : ''}`} aria-hidden="true">
            <ArrowDropUpIcon
                className={`${classes.sortIcon} ${active && order === 'asc' ? classes.activeSortIcon : ''} ${active && order !== 'asc' ? classes.mutedSortIcon : ''} ${!active ? classes.inactiveSortIcon : ''}`}
            />
            <ArrowDropDownIcon
                className={`${classes.sortIcon} ${active && order === 'desc' ? classes.activeSortIcon : ''} ${active && order !== 'desc' ? classes.mutedSortIcon : ''} ${!active ? classes.inactiveSortIcon : ''}`}
            />
        </span>
    );
}

function getEpisodeReportNumber(metadata, episodeId, podcastName) {
    const episodeNumber = metadata?.episode_number;

    if (episodeNumber !== null && episodeNumber !== undefined && episodeNumber !== '') {
        return episodeNumber;
    }

    return getEpisodeNumber(episodeId, podcastName);
}

function compareEpisodeRowsDescending(a, b) {
    const aNumber = Number(a.episodeNumber);
    const bNumber = Number(b.episodeNumber);
    const aHasNumeric = Number.isFinite(aNumber);
    const bHasNumeric = Number.isFinite(bNumber);

    if (aHasNumeric && bHasNumeric && aNumber !== bNumber) {
        return bNumber - aNumber;
    }

    if (aHasNumeric && !bHasNumeric) {
        return -1;
    }

    if (!aHasNumeric && bHasNumeric) {
        return 1;
    }

    return compareEpisodeNumbersDescending(a.id, b.id, '');
}

function parseDurationSeconds(value) {
    if (!value || value === '-') {
        return null;
    }

    const parts = String(value).split(':').map((part) => Number(part));
    if (parts.some((part) => !Number.isFinite(part))) {
        return null;
    }

    if (parts.length === 3) {
        return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
    }

    if (parts.length === 2) {
        return (parts[0] * 60) + parts[1];
    }

    return null;
}

function isEmptySortValue(value) {
    return value === null || value === undefined || value === '' || value === '-';
}

function compareSortableRows(a, b, column, order) {
    const direction = order === 'asc' ? 1 : -1;
    const aRaw = a[column.key];
    const bRaw = b[column.key];
    const aEmpty = isEmptySortValue(aRaw);
    const bEmpty = isEmptySortValue(bRaw);

    if (aEmpty && !bEmpty) return 1;
    if (!aEmpty && bEmpty) return -1;
    if (aEmpty && bEmpty) return 0;

    if (column.sortType === 'episodeNumber') {
        const aNumber = Number(aRaw);
        const bNumber = Number(bRaw);
        const aHasNumeric = Number.isFinite(aNumber);
        const bHasNumeric = Number.isFinite(bNumber);

        if (aHasNumeric && !bHasNumeric) return -1;
        if (!aHasNumeric && bHasNumeric) return 1;
        if (aHasNumeric && bHasNumeric && aNumber !== bNumber) {
            return (aNumber - bNumber) * direction;
        }
    }

    if (column.sortType === 'number') {
        const aNumber = Number(aRaw);
        const bNumber = Number(bRaw);
        if (Number.isFinite(aNumber) && Number.isFinite(bNumber) && aNumber !== bNumber) {
            return (aNumber - bNumber) * direction;
        }
    }

    if (column.sortType === 'date') {
        const aDate = Date.parse(aRaw);
        const bDate = Date.parse(bRaw);
        if (Number.isFinite(aDate) && Number.isFinite(bDate) && aDate !== bDate) {
            return (aDate - bDate) * direction;
        }
    }

    if (column.sortType === 'duration') {
        const aDuration = parseDurationSeconds(aRaw);
        const bDuration = parseDurationSeconds(bRaw);
        const aHasDuration = aDuration !== null;
        const bHasDuration = bDuration !== null;

        if (aHasDuration && !bHasDuration) return -1;
        if (!aHasDuration && bHasDuration) return 1;
        if (aHasDuration && bHasDuration && aDuration !== bDuration) {
            return (aDuration - bDuration) * direction;
        }
    }

    return String(aRaw).localeCompare(String(bRaw), undefined, {
        numeric: true,
        sensitivity: 'base',
    }) * direction;
}

function aggregateEpisodes(results, selectedEpisodeIds, episodeMetadata, podcastName) {
    if (!results) {
        return [];
    }

    const selectedEpisodeIdSet = new Set(selectedEpisodeIds.map(String));
    const totalsByEpisode = Object.values(results).reduce((acc, weekData) => {
        Object.entries(weekData).forEach(([episodeId, platforms]) => {
            if (selectedEpisodeIdSet.size > 0 && !selectedEpisodeIdSet.has(String(episodeId))) {
                return;
            }

            if (!acc[episodeId]) {
                acc[episodeId] = {};
            }

            TABLE_PLATFORM_KEYS.forEach((platform) => {
                const value = Number(platforms?.[platform] || 0);
                acc[episodeId][platform] = (acc[episodeId][platform] || 0) + value;
            });
        });

        return acc;
    }, {});

    const episodeMetadataMap = getEpisodeMetadataMap(episodeMetadata);

    return Object.entries(totalsByEpisode)
        .map(([episodeId, totals]) => {
            const total = TABLE_PLATFORM_KEYS.reduce((sum, platform) => sum + Number(totals[platform] || 0), 0);
            const metadata = episodeMetadataMap[String(episodeId)] || {};

            return {
                id: episodeId,
                episodeNumber: getEpisodeReportNumber(metadata, episodeId, podcastName),
                seasonNumber: metadata.season_number || '-',
                episodeTitle: metadata.name || '',
                duration: metadata.duration || '-',
                release_date: metadata.release_date || '',
                blubrry: totals.blubrry || 0,
                spotify: totals.spotify || 0,
                apple: totals.apple || 0,
                total,
            };
        })
        .filter((episode) => episode.total > 0)
        .sort(compareEpisodeRowsDescending);
}

export default function EpisodesTableView() {
    const classes = useStyles();
    const podcast = useAppState(s => s.podcast?.name);
    const episodeMode = useAppState(s => s.episodeMode);
    const selectedEpisodeIds = useAppState(s => s.selectedEpisodeIds);
    const intervalPreset = useAppState(s => s.intervalPreset);
    const intervalStartWeek = useAppState(s => s.intervalStartWeek);
    const intervalEndWeek = useAppState(s => s.intervalEndWeek);
    const intervalStartDate = useAppState(s => s.intervalStartDate);
    const intervalEndDate = useAppState(s => s.intervalEndDate);
    const episodeMetadata = useAppState(s => s.episodes);
    const [rawData, setRawData] = useState(false);
    const [orderBy, setOrderBy] = useState('episodeNumber');
    const [order, setOrder] = useState('desc');
    const selectionRequest = getEpisodeSelectionRequest(episodeMode, selectedEpisodeIds);
    const selectedEpisodeParam = selectionRequest.episodes;
    const { revision, acknowledgeAfterPaint } = useEpisodeSelectionConsumer('episodes-table');

    const episodesRequest = useAxios(
        ["episodes_table", podcast, episodeMode, selectedEpisodeParam, intervalPreset, intervalStartWeek, intervalEndWeek],
        {
            method: 'get',
            url: `/api/allepisodes/`,
            params: {
                episodes: selectedEpisodeParam,
                date_from: intervalStartWeek,
                date_to: intervalEndWeek,
                podcast: podcast
            }
        },
        { enabled: Boolean(podcast) && selectionRequest.enabled },
        () => {},
        () => {}
    );

    useEffect(() => {
        if (episodeMode === EPISODE_MODE_NONE) {
            setRawData(false);
            acknowledgeAfterPaint(revision);
            return;
        }

        if (episodesRequest.isFetching) {
            return;
        }

        if (episodesRequest.isError) {
            setRawData(false);
            acknowledgeAfterPaint(revision);
            return;
        }

        if (episodesRequest.data) {
            setRawData(episodesRequest.data.data);
            acknowledgeAfterPaint(revision);
        }
    }, [
        acknowledgeAfterPaint,
        episodeMode,
        episodesRequest.data,
        episodesRequest.isError,
        episodesRequest.isFetching,
        revision,
    ]);

    const rows = useMemo(
        () => episodeMode === EPISODE_MODE_NONE
            ? []
            : aggregateEpisodes(rawData?.results, selectedEpisodeIds, episodeMetadata, podcast),
        [episodeMode, rawData, selectedEpisodeIds, episodeMetadata, podcast]
    );
    const sortedRows = useMemo(() => {
        const column = TABLE_COLUMNS.find((item) => item.key === orderBy) || TABLE_COLUMNS[0];
        return [...rows].sort((a, b) => compareSortableRows(a, b, column, order));
    }, [rows, orderBy, order]);

    const handleSort = (columnKey) => {
        if (orderBy === columnKey) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
            return;
        }

        setOrderBy(columnKey);
        setOrder('asc');
    };

    const handleDownloadCsv = () => {
        const range = getCsvDateRange({
            intervalPreset,
            intervalStartDate,
            intervalEndDate,
            collectedStartWeek: rawData?.minDateValue,
            collectedEndWeek: rawData?.maxDateValue,
            rows,
            rowDateKey: 'release_date',
        });

        downloadGraphCsv({
            rows,
            columns: TABLE_COLUMNS,
            filename: buildGraphCsvFilename({
                podcast,
                startDate: range.startDate,
                endDate: range.endDate,
                graph: 'episodes',
            }),
        });
    };

    const isLoading = episodesRequest.isLoading;
    const isFetching = episodesRequest.isFetching;

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography align="left" variant="h4">Episodes</Typography>
                <MuiTooltip title="Download .csv">
                    <span>
                        <IconButton
                            aria-label="Download .csv"
                            className={classes.downloadButton}
                            color="primary"
                            disabled={rows.length === 0}
                            onClick={handleDownloadCsv}
                        >
                            <GetAppIcon />
                        </IconButton>
                    </span>
                </MuiTooltip>
            </Box>
            {isFetching && <LinearProgress />}
            {episodeMode === EPISODE_MODE_NONE ? (
                <Box className={classes.emptyState}>
                    <Typography>Select at least one episode.</Typography>
                </Box>
            ) : isLoading && rows.length === 0 ? (
                <Box className={classes.loadingState}>
                    <CircularProgress size={28} />
                </Box>
            ) : rows.length === 0 ? (
                <Box className={classes.emptyState}>
                    <Typography>No episodes with downloads in the selected interval.</Typography>
                </Box>
            ) : (
                <TableContainer className={classes.tableContainer}>
                    <Table size="small" aria-label="Episodes downloads table">
                        <TableHead>
                            <TableRow>
                                {TABLE_COLUMNS.map((column) => (
                                    <TableCell
                                        key={column.key}
                                        className={`${classes.tableHeadCell} ${orderBy === column.key ? classes.activeTableHeadCell : ''} ${getColumnClasses(column, classes)}`}
                                        onClick={() => handleSort(column.key)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                handleSort(column.key);
                                            }
                                        }}
                                        role="button"
                                        sortDirection={orderBy === column.key ? order : false}
                                        tabIndex={0}
                                    >
                                        <span className={classes.tableHeadContent}>
                                            <span className={classes.tableHeadLabel}>
                                                {renderHeader(column.header)}
                                            </span>
                                            <SortIndicator
                                                active={orderBy === column.key}
                                                classes={classes}
                                                order={order}
                                            />
                                        </span>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedRows.map((row) => (
                                <TableRow key={row.id} hover>
                                    {TABLE_COLUMNS.map((column) => (
                                        <TableCell
                                            key={`${row.id}-${column.key}`}
                                            className={getColumnClasses(column, classes)}
                                        >
                                            {row[column.key]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
